import React from "react";
import { Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";

export default function Mission() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-bark-mist via-white to-bark-mist text-bark-ink">
      <SiteHeader />

      <main>
        {/* Hero Section */}
        <section className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 md:grid-cols-2 md:px-8 md:py-24">
          <div>
            <div className="mb-8 inline-flex rounded-full bg-white px-5 py-2 text-sm font-bold uppercase tracking-widest text-bark-teal shadow-bark-soft">
              Our Mission
            </div>

            <h1 className="text-4xl font-extrabold leading-tight md:text-6xl">
              Helping dogs find friends while building a community that supports
              veterans, shelters, and adoption.
            </h1>

            <p className="mt-8 text-lg leading-8 text-bark-muted md:text-xl">
              Bark Buddies was created to help dogs and people build real-world
              connections. At its heart, Bark Buddies is a dog playdate app that
              helps dog owners create dog profiles, find compatible nearby dogs,
              message safely, schedule meetups, and discover local dog parks.
            </p>

            <p className="mt-6 text-lg leading-8 text-bark-muted md:text-xl">
              But Bark Buddies was built with a bigger purpose. Dogs have the
              power to change lives. They help people get outside, build
              routines, reduce loneliness, meet new people, and feel connected
              to something bigger than themselves. For many veterans, dogs can
              also provide comfort, structure, companionship, and a bridge back
              into community.
            </p>
          </div>

          <div className="relative mt-10 md:mt-0">
            <div className="absolute -inset-4 rounded-[2rem] bg-bark-teal/20 blur-2xl" />

            <img
              src="/assets/Founder.png"
              alt="Bark Buddies founder with two happy dogs"
              className="relative w-full rounded-[2rem] object-cover shadow-bark-card"
            />
          </div>
        </section>

        {/* Mission Cards */}
        <section className="mx-auto grid max-w-6xl gap-6 px-5 pb-16 md:grid-cols-2 md:px-8">
          <div className="rounded-3xl bg-white p-8 shadow-bark-card">
            <h2 className="text-2xl font-extrabold text-bark-ink">
              Veterans
            </h2>

            <p className="mt-4 leading-7 text-bark-muted">
              We want to help veterans connect with dog-friendly communities,
              walking groups, future events, and programs that strengthen the
              bond between veterans and dogs.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 shadow-bark-card">
            <h2 className="text-2xl font-extrabold text-bark-ink">
              Animal Shelters
            </h2>

            <p className="mt-4 leading-7 text-bark-muted">
              We want to support local animal shelters by helping promote
              adoptable dogs, increasing community visibility, and creating
              future partnerships that help more dogs find loving homes.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 shadow-bark-card">
            <h2 className="text-2xl font-extrabold text-bark-ink">
              Dog Adoption
            </h2>

            <p className="mt-4 leading-7 text-bark-muted">
              We believe every dog deserves a chance to be seen, loved, and
              matched with the right family. As Bark Buddies grows, we plan to
              create features and campaigns that help highlight adoptable dogs
              and encourage responsible adoption.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-8 shadow-bark-card">
            <h2 className="text-2xl font-extrabold text-bark-ink">
              Local Community
            </h2>

            <p className="mt-4 leading-7 text-bark-muted">
              Bark Buddies is about more than dog playdates. It is about
              helping people get outside, meet neighbors, build friendships,
              and create safer, more meaningful connections through their dogs.
            </p>
          </div>
        </section>

        {/* Future Vision */}
        <section className="mx-auto max-w-5xl px-5 pb-20 md:px-8">
          <div className="rounded-[2rem] bg-bark-ink p-8 text-white shadow-bark-card md:p-12">
            <h2 className="text-3xl font-extrabold">Our Future Vision</h2>

            <p className="mt-6 leading-8 text-white/85">
              Our long-term goal is to host and support community events such
              as dog walks, shelter adoption events, veteran dog meetups,
              fundraising events, and local partnerships with shelters,
              trainers, groomers, pet businesses, and veteran organizations.
            </p>

            <p className="mt-6 leading-8 text-white/85">
              We are still in the early stages, but the mission is clear: build
              a profitable, sustainable business that can grow into a real force
              for good.
            </p>

            <p className="mt-6 leading-8 text-white/85">
              Bark Buddies is currently a mission-driven, for-profit startup.
              As the app grows and becomes profitable, our goal is to dedicate
              part of that success toward supporting veterans, shelters,
              adoption efforts, and dog-centered community programs.
            </p>

            <div className="mt-8 rounded-2xl bg-white/10 p-6 text-xl font-bold">
              Every walk has the power to change two lives — your dog’s, and
              someone else’s.
            </div>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/beta"
                className="rounded-full bg-bark-teal px-7 py-4 text-center font-bold text-white hover:bg-bark-teal/90"
              >
                Join the Android Beta
              </Link>

              <a
                href="https://apps.apple.com/us/app/bark-buddies/id6788568836"
                target="_blank"
                rel="noreferrer"
                className="rounded-full bg-white px-7 py-4 text-center font-bold text-bark-ink hover:bg-white/90"
              >
                Download on iOS
              </a>
            </div>
          </div>
        </section>

        {/* Kickstarter Note */}
        <section className="mx-auto max-w-5xl px-5 pb-24 md:px-8">
          <div className="rounded-3xl border border-bark-teal/20 bg-white/80 p-8 shadow-bark-soft backdrop-blur">
            <h2 className="text-2xl font-extrabold text-bark-ink">
              Building a Mission-Driven Business
            </h2>

            <p className="mt-4 leading-7 text-bark-muted">
              Bark Buddies is not currently a nonprofit. It is a
              mission-driven, for-profit startup focused on building a
              sustainable platform first so that it can create lasting impact
              later. The goal is to grow Bark Buddies into a business that can
              support real-world programs for dogs, veterans, shelters,
              adoption efforts, and local communities.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}